
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Stats {
  totalReservations: number;
  totalReviews: number;
  totalImages: number;
  averageRating: number;
  recentReservations: any[];
  popularPartners: any[];
}

export default function AdminStats() {
  const [stats, setStats] = useState<Stats>({
    totalReservations: 0,
    totalReviews: 0,
    totalImages: 0,
    averageRating: 0,
    recentReservations: [],
    popularPartners: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // 예약 통계
      const { data: reservations } = await supabase
        .from('reservations')
        .select('*')
        .order('created_at', { ascending: false });

      // 리뷰 통계
      const { data: reviews } = await supabase
        .from('reviews')
        .select('*');

      // 이미지 통계
      const { data: images } = await supabase
        .from('partner_images')
        .select('*');

      // 최근 예약 5개
      const recentReservations = reservations?.slice(0, 5) || [];

      // 평균 평점 계산
      const averageRating = reviews && reviews.length > 0
        ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
        : 0;

      // 인기 업체 (리뷰 수 기준)
      const partnerReviewCounts = reviews?.reduce((acc: any, review) => {
        acc[review.partner_name] = (acc[review.partner_name] || 0) + 1;
        return acc;
      }, {}) || {};

      const popularPartners = Object.entries(partnerReviewCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a: any, b: any) => b.count - a.count)
        .slice(0, 5);

      setStats({
        totalReservations: reservations?.length || 0,
        totalReviews: reviews?.length || 0,
        totalImages: images?.length || 0,
        averageRating,
        recentReservations,
        popularPartners
      });
    } catch (error) {
      console.error('통계 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <i className="ri-loader-4-line text-2xl text-emerald-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">대시보드 통계</h2>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">총 예약</p>
              <p className="text-2xl font-bold">{stats.totalReservations}</p>
            </div>
            <i className="ri-calendar-line text-3xl text-blue-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">총 리뷰</p>
              <p className="text-2xl font-bold">{stats.totalReviews}</p>
            </div>
            <i className="ri-star-line text-3xl text-green-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">갤러리 이미지</p>
              <p className="text-2xl font-bold">{stats.totalImages}</p>
            </div>
            <i className="ri-image-line text-3xl text-purple-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">평균 평점</p>
              <p className="text-2xl font-bold">{stats.averageRating.toFixed(1)}</p>
            </div>
            <i className="ri-trophy-line text-3xl text-yellow-200"></i>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 최근 예약 */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">최근 예약</h3>
          {stats.recentReservations.length === 0 ? (
            <p className="text-gray-500 text-center py-4">예약이 없습니다.</p>
          ) : (
            <div className="space-y-3">
              {stats.recentReservations.map((reservation, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{reservation.name}</p>
                      <p className="text-sm text-gray-600">{reservation.email}</p>
                      <p className="text-sm text-gray-500">{reservation.program}</p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(reservation.created_at).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 인기 업체 */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">인기 업체 (리뷰 수)</h3>
          {stats.popularPartners.length === 0 ? (
            <p className="text-gray-500 text-center py-4">리뷰가 없습니다.</p>
          ) : (
            <div className="space-y-3">
              {stats.popularPartners.map((partner: any, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">{partner.name}</span>
                    <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-sm">
                      {partner.count}개 리뷰
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
